import { clearDocument } from 'y-indexeddb';

import { CLEAR_TIMEOUT_MS, MAX_INDEXEDDB_USAGE_RATIO, MIN_INDEXEDDB_FREE_BYTES } from './constants';

/**
 * Returns false when we cannot verify origin storage headroom, or when storage is
 * nearly full, so we skip y-indexeddb before `indexedDB.open` can throw
 * QuotaExceededError (e.g. user disk full). Local IDB is optional — server state
 * is authoritative — so we fail closed when `estimate` is missing or inconclusive.
 */
export const hasIndexedDBStorageHeadroom = async (): Promise<boolean> => {
  if (!navigator.storage?.estimate) {
    console.warn('[BrainfishEditor] storage.estimate unavailable; skipping IndexedDB persistence');

    return false;
  }

  try {
    const { quota, usage } = await navigator.storage.estimate();
    if (quota == null || usage == null) {
      console.warn('[BrainfishEditor] storage.estimate missing quota/usage; skipping IndexedDB persistence');

      return false;
    }

    const free = quota - usage;
    if (free < MIN_INDEXEDDB_FREE_BYTES || usage / quota > MAX_INDEXEDDB_USAGE_RATIO) {
      console.warn('[BrainfishEditor] Low origin storage; skipping IndexedDB persistence', {
        quota,
        usage,
        free,
      });

      return false;
    }

    return true;
  } catch (e) {
    console.warn('[BrainfishEditor] storage.estimate failed; skipping IndexedDB persistence', e);

    return false;
  }
};

/**
 * Clear stale IndexedDB Y.Doc cache before connecting to Hocuspocus.
 * Platform persists authoritative state on the server; merging local IDB + server
 * snapshots on refresh duplicates document content in the Y.Doc.
 *
 * Uses `clearDocument` (deleteDB) so we never open/load the stale cache first.
 * Returns true only when the DB was deleted; callers must not attach
 * IndexeddbPersistence over an uncleared room.
 */
export const clearIndexedDBCacheForRoom = async (room: string): Promise<boolean> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  try {
    await Promise.race([
      clearDocument(room),
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(`IndexedDB clear timed out for room: ${room}`));
        }, CLEAR_TIMEOUT_MS);
      }),
    ]);

return true;
  } catch (e) {
    console.warn(`[BrainfishEditor] IndexedDB clear failed for room: ${room}`, e);

return false;
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
};
